
import Vue, { PropType } from 'vue'
import { mapGetters } from 'vuex'
import { RCard, RButton } from '@/components'
import {
  ConfirmSubscriptionCancelModal,
  ConfirmSubscriptionCancelBlockedModal,
  SubscriptionCancelSuccessfulModal,
} from '@/components/modals'

import { SelectedStorageTier, UserProxy, BundlePeriod } from '@/api/types/user'
import { RequestInfo } from '@/store/types/root'
import { StoragePlan } from '@/store/types/user'

import { getBundleOfferDataByCodename, BundleOfferInfo } from '@/services/bundles'
import { formatPriceFromCents } from '@/services/prices'
import { milisecondsToDateString } from '@/services/date'
import { upperCaseFirstCharacter } from '@/services/strings'
import { getFeaturesString } from '@/services/bundles'
import { secondsToMiliseconds } from '@/services/time'
import { getAppRootURL } from '@/config'

export default Vue.extend({
  head: {
    link: [{ rel: 'canonical', href: `${getAppRootURL()}/account/overview`, id: 'canonical' }],
  },
  components: {
    RCard,
    RButton,
    ConfirmSubscriptionCancelModal,
    ConfirmSubscriptionCancelBlockedModal,

    SubscriptionCancelSuccessfulModal,
  },
  props: {
    user: {
      type: Object as PropType<UserProxy>,
    },
    hasPaymentMethod: {
      type: Boolean,
    },
    subscriptionCancelRequest: {
      type: RequestInfo,
    },
    storagePlan: {
      type: Object as PropType<StoragePlan>,
    },
    selectedTier: {
      type: Object as PropType<SelectedStorageTier>,
    },
    showChangeButton: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    // GETTERS
    creditsRemainingDollars(): string {
      return formatPriceFromCents(this.creditsRemainingCents)
    },
    hasAPlan(): boolean {
      return Boolean(this.selectedTier) && Boolean(this.selectedTier.codename)
    },
    accountType(): string {
      return upperCaseFirstCharacter(this.user.taxType)
    },
    bundlePeriod(): BundlePeriod {
      return this.selectedTier?.bundle_period === 'year' ? 'year' : 'month'
    },
    /**
     * Should include the additional payments for extra workspaces etc. (so count in limits?)
     */
    planPrice(): number {
      const { planDetails, bundlePeriod } = this
      if (planDetails && bundlePeriod && planDetails[bundlePeriod]) {
        return parseInt(planDetails[bundlePeriod]!.priceUsd)
      } else if (planDetails) {
        return parseInt(planDetails.priceUsdPerMonth)
      }
      return 0
    },
    planName(): string | undefined {
      if (this.planDetails?.name) {
        return upperCaseFirstCharacter(this.planDetails?.name)
      }
      return 'No plan selected'
    },
    planDetails(): BundleOfferInfo | undefined {
      const bundleData = getBundleOfferDataByCodename(this.bundles, this.selectedTier?.codename)
      return bundleData
    },

    // TEMPLATE STRINGS
    paymentBtnTxt(): string {
      return this.hasPaymentMethod ? 'Update card' : 'Add card'
    },
    billingPeriodInfo(): string {
      if (!this.billingPeriodObject) return ''
      if (this.billingPeriodObject.isTrialOngoing) {
        const trialEndDateString = milisecondsToDateString(
          secondsToMiliseconds(this.billingPeriodObject.trialEnd),
        )
        return `Your trial period ends on ${trialEndDateString}.`
      }
      if (this.upcomingTier.codename) {
        return `Your plan is set to downgrade from the X plan to the ${this.upcomingTier.codename} plan on 
        ${this.upcomingTier.upcoming_subs_start_sec_timestamp}`
      }
      return `Your plan was activated on ${this.billingPeriodObject.start} and renews on ${this.billingPeriodObject.end}.`
    },
    totalString(): string {
      return `$${this.planPrice}/${this.bundlePeriod} + VAT`
    },
    planFeaturesString(): string {
      if (!this.planDetails?.features) return ''
      return getFeaturesString(this.planDetails.features)
    },

    ...mapGetters({
      bundles: 'config/bundles',
      redeemCodeRequest: 'payment/redeemCodeRequest',
      hasRunningWorkstations: 'workstation/hasRunningWorkstations',
      isRegionDisabled: 'config/isRegionDisabled',
      billingPeriodObject: 'user/billingPeriodObject',
      upcomingTier: 'user/upcomingTier',
      creditsRemainingCents: 'payment/creditsInCents',
    }),
  },
  watch: {
    subscriptionCancelRequest(value: RequestInfo) {
      if (value.successful) {
        this.$rModal.hide('cancelSubscriptionSuccess')
      }
    },
  },
})
